/**
 * @file InvoiceModal.js
 * @version 2.20.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-02
 * @modified 2026-11-03
 * @description Rich invoice preview modal with structured data, line items, and PDF download.
 *              Follows PurchaseOrderModal pattern. Surfaces cash payments received;
 *              linked documents and credit allocations are the shared
 *              LinkedDocuments panel.
 *
 * 2.11.0 (2026-10-07): a till invoice or credit note offers "Change Customer"
 * (ChangeCustomerModal, disabled with its reason when it cannot) and lists the
 * changes made.
 *
 * 2.12.0 (2026-10-08): "Add Credit" and "Add Debit" in the footer
 * (utils/invoiceAdjustment) — Create Invoice pre-filled from this invoice,
 * linked to it and (a credit) allocated to it on save. On every document,
 * disabled with the reason where it does not apply (credit notes, till,
 * voided, view-only, no "Create invoices", nowhere to navigate).
 *
 * 2.13.0 (2026-10-19, Cam): "Edit" in the footer — Create Invoice (or Create
 * Credit Note) opened on this document, as Search Invoices' Edit
 * (utils/editHandoff). Greyed with the server's reason (editBlock — the rule
 * the save uses), view-only, or with nowhere to navigate.
 *
 * 2.13.1 (2026-10-20, Cam): each line's quantity shows in its own unit with
 * the tonnes beneath (QtyWithTonnes) — invoices, credit notes and HW till
 * invoices alike.
 *
 * 2.15.0 (2026-10-28, Cam — units clear before November's invoices): each
 * line's unit price says what it is per ("£400.00/T", "£10.00/25kg bag" with
 * its figure a tonne, "unit not set") — components/PriceWithUnit.
 * 2.14.0 (2026-10-28, Cam — rep mobile review): on a rep's phone the lines
 * are blocks (RepLineBlock — the Sales Orders card's line) instead of the
 * seven-column table that ran off the side; the details are label / value
 * rows and Close stays in view (RepMobile.css). Desktop unchanged.
 * 2.16.0 (2026-11-01, Cam — units corrected after an invoice was paid): the
 * window shows the invoice's "Needs correcting" flags (InvoiceCorrectionPanel
 * — what changed, the money difference, Xero's lock) with "Dealt with…".
 * 2.17.0 (2026-11-03, Cam — approved here, not yet in Xero): HeldHerePanel —
 * a held invoice says who held it, when and Xero's reason, with "Send to Xero
 * now"; an approved invoice Xero refused on its way offers "Keep held here".
 * Push to Xero now reads the response's own fields (xeroInvoiceId,
 * xeroPushError — it read snake_case names the response never had, so it
 * showed on every approved invoice) and is not shown on a held one (its own
 * panel sends it).
 * 2.18.0 (2026-11-03, Cam — the Invoice Detail variation review): the
 * window shows the invoice's open variation flags (InvoiceVariationFlagPanel
 * — the note, who flagged it) with "Clear flag" for the person who flagged
 * it (or an admin); a draft's approves it again.
 * 2.19.0 (2026-11-03, Cam — booking over an invoice already raised): "Book
 * over" in the footer (BookOverInvoiceFlow; greyed with the server's
 * `bookOverBlock`), and a line saying which booking it is part of ("Booked
 * over — part 1 of 3", the other parts opening their invoices).
 * 2.20.0 (2026-11-03, Cam — till sales booked to account, and notes in the
 * variation review): the window lists the variation review's notes on this
 * invoice (InvoiceVariationNotesPanel — why a variation was okayed, what was
 * done) and says when a till invoice was edited here and no longer matches
 * its till sale. Its Edit follows the server's editBlock, which now lets a
 * till invoice booked to account (not paid at the till) through.
 */

import React, { useState, useEffect, useContext } from 'react';
import PriceWithUnit from './PriceWithUnit';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './InvoiceModal.css';
import { showAlert } from './Alert';
import LoadingSpinner from './LoadingSpinner';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import QtyWithTonnes from './QtyWithTonnes';
import { formatDateShort } from '../utils/dateFormatters';
import { getTaxPercentDisplay } from '../utils/taxTypes';
import { useSlideOutButton } from './SlideOutButton';
import AuditHistoryModal from './AuditHistoryModal';
import useOpenInSearch from '../hooks/useOpenInSearch';
import DocumentOutputModal from './DocumentOutputModal';
import LinkedDocuments from './LinkedDocuments';
import ChangeCustomerModal from './ChangeCustomerModal';
import { describeCustomerChange } from '../utils/customerChange';
import { SearchRevealContext } from '../hooks/useOpenInSearch';
import { useAccess, useReadOnly } from '../hooks/useAccess';
import { ADJUST_ACTIONS, startAdjustment, adjustActionBlock } from '../utils/invoiceAdjustment';
import { startEdit } from '../utils/editHandoff';
import useRepMobile from '../hooks/useRepMobile';
import RepLineBlock, { RepLineBlocks } from './RepLineBlock';
import { perUnitText } from '../utils/repOrderCard';
import InvoiceCorrectionPanel from './InvoiceCorrectionPanel';
import HeldHerePanel from './HeldHerePanel';
import InvoiceVariationFlagPanel from './InvoiceVariationFlagPanel';
import BookOverInvoiceFlow from './BookOverInvoiceFlow';
import InvoiceVariationNotesPanel from './InvoiceVariationNotesPanel';

function InvoiceModal({ invoiceId, onClose, onOpenInSearch, onNavigate }) {
  // A rep's phone: the lines as blocks rather than the table (2.14.0).
  const repMobile = useRepMobile();
  // "Find in …" wherever this was opened from (hooks/useOpenInSearch).
  const { open: openInSearch } = useOpenInSearch('invoice', onOpenInSearch, onClose);
  // Add Credit / Add Debit go to Create Invoice through the shell's navigation.
  const shellNavigate = useContext(SearchRevealContext)?.onNavigate;
  const navigate = typeof onNavigate === 'function' ? onNavigate : shellNavigate;
  const access = useAccess();
  const { readOnly, reason: readOnlyReason } = useReadOnly();
  const adjustAccess = {
    canCreate: access.can('invoices.create'), readOnly, readOnlyReason, canNavigate: typeof navigate === 'function'
  };
  const [invoice, setInvoice] = useState(null);
  const [payments, setPayments] = useState([]); // cash receipts against this invoice
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  // Export (DocumentOutputModal): preview, then print, download or email to
  // yourself. It replaced the header's download-PDF buttons.
  const [exporting, setExporting] = useState(false);
  const [pushingToXero, setPushingToXero] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // "Change customer…" on a till document (ChangeCustomerModal).
  const [changingCustomer, setChangingCustomer] = useState(false);
  // "Book over…" — move it to another date or split it, while unpaid (BookOverInvoiceFlow).
  const [bookingOver, setBookingOver] = useState(false);

  // Re-measure once the invoice loads — the "Find in Search Invoices"
  // button only renders then, and an unmeasured slide-out stays collapsed.
  useSlideOutButton([invoice, loading, pushingToXero, showHistory]);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails();
      document.body.classList.add('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) contentWrapper.classList.add('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) contentWrapper.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // Safe JSON parser — falls back to a structured error when the body isn't
  // JSON (e.g. when an upstream proxy or rate-limiter returns plain text).
  const safeJson = async (response) => {
    const text = await response.text();
    try {
      return { ok: response.ok, status: response.status, body: JSON.parse(text) };
    } catch {
      return {
        ok: response.ok,
        status: response.status,
        body: { success: false, error: text || `Server returned ${response.status}` }
      };
    }
  };

  // A local-only invoice has never reached Xero — usually because the push
  // failed at creation, or because Xero was disconnected at the time. There
  // was previously no way to retry one from the front-end outside Till Sales.
  const handlePushToXero = async () => {
    setPushingToXero(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/push-to-xero`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to push to Xero');
      }
      if (data.skipped) {
        showAlert(`${invoice?.invoice_no || 'Invoice'} was not sent to Xero: ${data.reason || 'nothing to send'}.`, 'info');
      } else {
        showAlert(`${invoice?.invoice_no || 'Invoice'} ${data.linked ? 'was already in Xero and is now linked to it' : 'pushed to Xero'}.`, 'success');
      }
      await fetchInvoiceDetails();
    } catch (error) {
      showAlert(`Could not push to Xero: ${error.message}`, 'error');
      // The failure is written on the invoice; show it.
      await fetchInvoiceDetails();
    } finally {
      setPushingToXero(false);
    }
  };

  const fetchInvoiceDetails = async () => {
    setPayments([]);
    try {
      // Ensure line items exist first. We don't actually use the response,
      // but we DO need to surface a friendly error if it fails (e.g. rate-limited).
      const ensureRes = await fetch(`${API_URL}/api/invoices/${invoiceId}/ensure-line-items`, {
        credentials: 'include'
      });
      if (ensureRes.status === 429) {
        showAlert('Server is busy — please wait a moment and try again.');
        return;
      }

      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}`, {
        credentials: 'include'
      });
      if (response.status === 429) {
        showAlert('Server is busy — please wait a moment and try again.');
        return;
      }

      const { body: data } = await safeJson(response);
      if (data.success) {
        setInvoice(data.invoice);
        // Non-fatal: cash receipts against this invoice. (Credit notes applied
        // to it are chips in the Linked documents panel.)
        try {
          const payRes = await fetch(`${API_URL}/api/payments/invoice/${invoiceId}`, {
            credentials: 'include'
          });
          if (payRes.ok) {
            const pj = await payRes.json();
            if (pj.success) setPayments(pj.payments || []);
          }
        } catch { /* ignore — payments block just won't render */ }
      } else {
        showAlert(`Failed to load invoice: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      showAlert('Failed to load invoice: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const contentWrapper = document.querySelector('.content-wrapper');
      if (contentWrapper) contentWrapper.classList.remove('modal-open');
      onClose();
    }, 300);
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'DRAFT': 'draft',
      'SUBMITTED': 'submitted',
      'AUTHORISED': 'authorised',
      'PAID': 'paid',
      'VOIDED': 'voided',
      'DELETED': 'deleted'
    };
    return `status-badge ${statusMap[status] || 'default'}`;
  };

  const isPaid = invoice && Math.abs(parseFloat(invoice.total || 0) - parseFloat(invoice.amountPaid || 0)) < 0.01;
  const isOverdue = invoice && !isPaid && invoice.dueDate && new Date(invoice.dueDate) < new Date();
  // Which stamps could apply to the downloaded PDF (matches server overdue rule).
  // HW- documents come from the Epos Now till at the H J Ward store. The detail
  // endpoint returns camelCase (invoiceNo); invoice_no is checked too because
  // this modal is opened from several callers and one of them still passes the
  // snake_case row through.
  const isStorePurchase = Boolean(
    (invoice?.invoiceNo || invoice?.invoice_no || '').startsWith('HW-')
  );

  if (!invoiceId) return null;

  return createPortal(
    <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`modal-content invoice-modal ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <h2>{loading ? 'Loading...' : `${invoice?.isCredit ? 'Credit Note' : 'Invoice'} ${invoice?.invoiceNo}`}</h2>
            {!loading && invoice && (
              <>
                <span className={getStatusBadgeClass(invoice.status)}>
                  {invoice.status}
                </span>
                {isOverdue && <span className="status-badge overdue">OVERDUE</span>}
              </>
            )}
          </div>
          <div className="header-actions">
            <button
              className="btn-icon"
              data-read-safe
              onClick={() => setExporting(true)}
              disabled={loading || !invoice?.id}
              title="Export — preview, then print, download or email to yourself"
            >
              <span className="material-symbols-rounded">ios_share</span>
            </button>
            <button className="btn-icon close-btn" onClick={handleClose} title="Close">
              <span className="material-symbols-rounded">close</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="modal-body">
            <LoadingSpinner />
          </div>
        ) : invoice ? (
          <div className="modal-body">
            {/* "Needs correcting" (2.16.0): units corrected on its order after it was paid. */}
            <InvoiceCorrectionPanel
              flags={invoice.correctionFlags || []}
              customerName={invoice.customer?.name}
              canResolve={!readOnly && (access.can('invoices.create') || access.can('invoices.edit'))}
              onResolved={() => fetchInvoiceDetails()}
            />
            {/* A part of a booking (booked over — 2.19.0). */}
            {(invoice.bookedOver || []).length > 0 && (() => {
              const mine = invoice.bookedOver.find(p => Number(p.id) === Number(invoice.id));
              const others = invoice.bookedOver.filter(p => Number(p.id) !== Number(invoice.id));
              return (
                <div className="invoice-booked-over" data-testid="invoice-booked-over">
                  <span className="material-symbols-rounded" aria-hidden="true">event_repeat</span>
                  <span>
                    {mine && mine.of > 1 ? `Booked over — part ${mine.part} of ${mine.of}` : 'Booked over'}
                    {mine && mine.note ? ` ("${mine.note}")` : ''}
                    {others.length ? `; the other part${others.length === 1 ? ' is' : 's are'} ${others.map(p => `${p.invoice_no} (${formatDateShort(p.invoice_date)})`).join(', ')} — see Linked documents` : ''}.
                  </span>
                </div>
              );
            })()}
            {/* Approved here, not yet in Xero (2.17.0). */}
            <HeldHerePanel
              invoice={invoice}
              canHold={!readOnly && access.can('invoices.create')}
              canSend={!readOnly && (access.can('invoices.create') || access.can('invoices.edit'))}
              whyNot={readOnly ? (readOnlyReason || 'View only') : null}
              onChanged={() => fetchInvoiceDetails()}
              notify={(message, type) => showAlert(message, type)}
            />
            {/* Flagged from the Invoice Detail variation review (2.18.0). */}
            <InvoiceVariationFlagPanel
              flags={invoice.variationFlags || []}
              readOnly={readOnly}
              readOnlyReason={readOnlyReason}
              onCleared={() => fetchInvoiceDetails()}
            />
            {/* The variation review's notes, and a till invoice edited here (2.19.0). */}
            <InvoiceVariationNotesPanel
              notes={invoice.variationNotes || []}
              tillEditedHereText={invoice.tillEditedHereText || null}
            />
            <div className="invoice-main-sections">
              <div className="invoice-details-section">
                <h3>Invoice Details</h3>
                <div className="invoice-details-grid">
                  <div className="detail-row">
                    <span className="label">Date:</span>
                    <span className="value">{formatDateShort(invoice.invoiceDate)}</span>
                  </div>
                  {invoice.dueDate && (
                    <div className="detail-row">
                      <span className="label">Due Date:</span>
                      <span className="value">{formatDateShort(invoice.dueDate)}</span>
                    </div>
                  )}
                  {invoice.reference && (
                    <div className="detail-row">
                      <span className="label">Reference:</span>
                      <span className="value">{invoice.reference}</span>
                    </div>
                  )}
                  {invoice.supplierReference && (
                    <div className="detail-row">
                      <span className="label">Supplier Reference:</span>
                      <span className="value">{invoice.supplierReference}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">Source:</span>
                    <span className="value" style={{ textTransform: 'capitalize' }}>{invoice.source}</span>
                  </div>
                  {/* Approval is a filterable column on the invoice search and
                      was absent from the record's own view — so the one place
                      you could see whether an invoice was approved was the list
                      you had just left. Every invoice carries a status, so this
                      is unconditional; who and when follow only once somebody
                      has actually approved it. */}
                  {invoice.approval?.status && (
                    <div className="detail-row">
                      <span className="label">Approval:</span>
                      <span className="value">
                        <span className={`approval-badge ${invoice.approval.status}`}>
                          {invoice.approval.status}
                        </span>
                        {invoice.approval.by && ` — ${invoice.approval.by}`}
                        {invoice.approval.at && ` on ${formatDateShort(invoice.approval.at)}`}
                      </span>
                    </div>
                  )}
                  {invoice.approval?.note && (
                    <div className="detail-row">
                      <span className="label">Approval note:</span>
                      <span className="value">{invoice.approval.note}</span>
                    </div>
                  )}
                  {/* A till document moved to another customer: each change,
                      who made it, when and why (newest first). */}
                  {Array.isArray(invoice.customerChanges) && invoice.customerChanges.slice().reverse().map((c, i) => (
                    <div className="detail-row" key={`cc-${c.at}-${i}`}>
                      <span className="label">{i === 0 ? 'Customer changed:' : ''}</span>
                      <span className="value">{describeCustomerChange(c).replace(/^Customer changed: /, '')}</span>
                    </div>
                  ))}
                  {invoice.trackingRep && (
                    <div className="detail-row">
                      <span className="label">Rep:</span>
                      <span className="value">{invoice.trackingRep}</span>
                    </div>
                  )}
                  {/* Notes was read into the response but never selected from
                      the table, so this row has rendered empty since it was
                      written — on 1,197 invoices that have one. */}
                  {invoice.notes && (
                    <div className="detail-row">
                      <span className="label">Notes:</span>
                      <span className="value">{invoice.notes}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="customer-section">
                <h3>To:</h3>
                <div className="address-block">
                  <div className='name-acc-no'>
                    <p className="contact-name">{invoice.customer.name}</p>
                    {invoice.customer.accountNo && (
                      <p className="account-no">{invoice.customer.accountNo}</p>
                    )}
                  </div>
                  {invoice.customer.address && (
                    <p style={{ whiteSpace: 'pre-line', marginTop: '4px', color: 'var(--text-secondary)' }}>
                      {[
                        invoice.customer.address.replace(/, /g, '\n'),
                        invoice.customer.city,
                        invoice.customer.postcode
                      ].filter(Boolean).join('\n')}
                    </p>
                  )}
                </div>
              </div>

              <div className="company-section">
                <h3>From:</h3>
                <div className="company-inner">
                  <div className="company-address">
                    <h3>Excel Agri Ltd.</h3>
                    <p style={{ whiteSpace: 'pre-line', color: 'var(--text-secondary)' }}>
                      {'The Old Grain Drier\nDolton Beacon\nWinkleigh\nDevon\nEX19 8PS'}
                    </p>
                  </div>
                  <div className="company-numbers">
                    <div className="number-item">
                      <span className="label">VAT Number</span>
                      <span className="value">375091584</span>
                    </div>
                    <div className="number-item">
                      <span className="label">UFAS Number</span>
                      <span className="value">6206 (31/01/27)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Its order, POs, the bills on them, other invoices of the order
                and the credits allocated to it (or, for a credit note, the
                invoices it went to) — one shared panel, the same in the order,
                PO and bill modals. Each chip opens that document on top. */}
            <LinkedDocuments type="invoice" id={invoice.id} refreshKey={invoice} onChange={fetchInvoiceDetails} />

            <div className="line-items-section">
              <h3>Line Items</h3>
              {repMobile ? (
                <RepLineBlocks empty="No line items">
                  {(invoice.lineItems || []).map((item, index) => {
                    const name = item.item_name || item.description;
                    const discount = item.discountAmount && parseFloat(item.discountAmount) > 0 ? formatCurrency(item.discountAmount) : '';
                    return (
                      <RepLineBlock
                        key={index}
                        code={item.itemCode || item.item_code}
                        name={name}
                        words={item.item_name && item.description
                          && !String(item.description).toLowerCase().includes(String(item.item_name).toLowerCase())
                          ? [item.description] : []}
                        facts={[
                          { key: 'qty', label: 'Quantity', value: <QtyWithTonnes quantity={item.quantity} line={item} /> },
                          {
                            key: 'price',
                            label: 'Price',
                            value: (
                              <span className="roc-fact-value">
                                {formatPrice(item.unitPrice)}
                                <span className="roc-fact-per"> {perUnitText(item)}</span>
                              </span>
                            )
                          },
                          { key: 'amount', label: 'Amount', align: 'right', value: <span className="roc-fact-value">{formatCurrency(item.lineTotal)}</span> }
                        ]}
                      >
                        <div className="rep-line-foot">
                          <span>VAT {getTaxPercentDisplay(item.taxType)}</span>
                          {discount && <span>Discount {discount}</span>}
                        </div>
                      </RepLineBlock>
                    );
                  })}
                </RepLineBlocks>
              ) : (
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Item Code</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Discount</th>
                    <th>VAT %</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems && invoice.lineItems.length > 0 ? (
                    invoice.lineItems.map((item, index) => (
                      <tr key={index}>
                        {/* Catalogue name, then what was actually typed
                            underneath where it differs — carried all the way
                            down from the order line, where somebody may have
                            appended a contract number or a vet reference. */}
                        <td>
                          <span className="inv-line-name">{item.item_name || item.description}</span>
                          {item.description && item.item_name
                            && !String(item.description).toLowerCase().includes(String(item.item_name).toLowerCase())
                            && <span className="inv-line-desc">{item.description}</span>}
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{item.itemCode || item.item_code || '-'}</td>
                        {/* Its own unit and the tonnes it comes to (utils/qtyWithTonnes). */}
                        <td><QtyWithTonnes quantity={item.quantity} line={item} /></td>
                        <td><PriceWithUnit value={item.unitPrice} line={item} /></td>
                        <td>{item.discountAmount && parseFloat(item.discountAmount) > 0 ? formatCurrency(item.discountAmount) : ''}</td>
                        <td>{getTaxPercentDisplay(item.taxType)}</td>
                        <td className="text-right">{formatCurrency(item.lineTotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-items">No line items</td>
                    </tr>
                  )}
                </tbody>
              </table>
              )}

              {/* Provenance note for till documents. HW- numbers come from the
                  Epos Now till at the H J Ward store, so the customer bought
                  over a counter rather than from Excel Agri directly. Keyed off
                  the invoice number, matching puppeteerPdfService._storeNote, so
                  the modal and the PDF say the same thing. */}
              {isStorePurchase && (
                <p className="store-note">Purchased from HJ Ward Agriculture store</p>
              )}
            </div>

            <div className="totals-section">
              <div className="totals-grid">
                <div className="total-row">
                  <span className="label">Subtotal:</span>
                  <span className="value">{formatCurrency(invoice.subtotal)}</span>
                </div>
                <div className="total-row">
                  <span className="label">VAT:</span>
                  <span className="value">{formatCurrency(invoice.tax)}</span>
                </div>
                <div className="total-row total">
                  <span className="label">Total:</span>
                  <span className="value">{formatCurrency(invoice.total)}</span>
                </div>
                {invoice.amountPaid > 0.01 && (
                  <>
                    <div className="total-row payments">
                      <span className="label">Less Payments:</span>
                      <span className="value">-{formatCurrency(invoice.amountPaid)}</span>
                    </div>
                    <div className="total-row balance">
                      <span className="label">Balance Due:</span>
                      <span className="value">{formatCurrency(invoice.balance)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {payments.length > 0 && (
              <div className="settlement-section">
                <h4 className="settlement-heading">
                  <span className="material-symbols-rounded">payments</span>
                  Payments Received
                </h4>
                <table className="settlement-table">
                  <tbody>
                    {payments.map((p) => (
                      <tr key={`pay-${p.id}`}>
                        <td className="settlement-method">
                          {p.bank_account_name || p.payment_type || 'Payment'}
                          {p.reference ? <span className="settlement-ref"> · {p.reference}</span> : null}
                        </td>
                        <td className="settlement-date">{formatDateShort(p.payment_date)}</td>
                        <td className="settlement-amount">-{formatCurrency(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="modal-body">
            <div className="error-state">
              Failed to load invoice details
            </div>
          </div>
        )}

        {/* Every button in here needs a loaded invoice, and Close — which did
            not — has gone to the header. So the footer itself is conditional
            now: while loading, or after a failed fetch, it would otherwise be
            an empty bar. */}
        {invoice && (
        <div className="modal-footer">
          <div className="buttons slideout">
            {/* Full change history for this document, including credit
                allocations — the same audit records the admin Activity
                dashboard reads, surfaced here for ordinary users. */}
            <div>
              <button className="btn-slideout" onClick={() => setShowHistory(true)}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">history</span>
                </div>
                <span className="btn-label">History</span>
              </button>
            </div>

            {/* Edit: the document in Create Invoice / Create Credit Note, as
                Search Invoices' Edit. Greyed with the reason it can't be. */}
            {(() => {
              const isCredit = !!(invoice.isCredit || invoice.is_credit);
              const block = readOnly ? (readOnlyReason || 'View only')
                : typeof navigate !== 'function' ? 'Open it from Search Invoices to edit it'
                  : (invoice.editBlock || null);
              return (
                <div>
                  <button data-write-action
                    className={`btn-slideout${block ? ' btn-disabled' : ''}`}
                    disabled={!!block}
                    title={block || `Edit this ${isCredit ? 'credit note' : 'invoice'}`}
                    onClick={() => { onClose(); startEdit(isCredit ? 'credit_note' : 'invoice', invoice.id, navigate); }}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">edit</span>
                    </div>
                    <span className="btn-label">Edit</span>
                  </button>
                </div>
              );
            })()}

            {/* Book over: to another date, or split over several invoices,
                while it is unpaid (services/invoiceBookOverService). Shown on
                every invoice, greyed with the server's reason. */}
            {!(invoice.isCredit || invoice.is_credit) && (() => {
              const block = readOnly ? (readOnlyReason || 'View only') : (invoice.bookOverBlock || null);
              return (
                <div>
                  <button data-write-action
                    className={`btn-slideout${block ? ' btn-disabled' : ''}`}
                    disabled={!!block}
                    title={block || 'Move this invoice to another date, or split it over several'}
                    onClick={() => setBookingOver(true)}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">event_repeat</span>
                    </div>
                    <span className="btn-label">Book over</span>
                  </button>
                </div>
              );
            })()}

            {/* Only for a document that never reached Xero. Drafts are excluded:
                they aren't issued yet, so they aren't expected in Xero. */}
            {invoice && !invoice.xeroInvoiceId && !invoice.heldHere
              && !['VOIDED', 'DELETED', 'DRAFT'].includes(String(invoice.status || '').toUpperCase()) && (
              <div>
                <button data-write-action
                  className="btn-slideout"
                  onClick={handlePushToXero}
                  disabled={pushingToXero}
                  title={invoice.xeroPushError
                    ? `Last attempt failed: ${invoice.xeroPushError}`
                    : 'This invoice has not reached Xero yet'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">cloud_upload</span>
                  </div>
                  <span className="btn-label">
                    {pushingToXero ? 'Pushing...' : 'Push to Xero'}
                  </span>
                </button>
              </div>
            )}

            {/* Till documents only: their customer is changed here (and in
                Xero) — the editor never changes it; one booked to account may
                also be edited (Edit, 2.19.0). Shown on every till document,
                disabled with the reason when it cannot be changed. */}
            {invoice.fromTill && (
              <div>
                <button data-write-action
                  className="btn-slideout"
                  onClick={() => setChangingCustomer(true)}
                  disabled={!!invoice.customerChangeBlock}
                  title={invoice.customerChangeBlock || 'Move this till document to the right customer, here and in Xero'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">swap_horiz</span>
                  </div>
                  <span className="btn-label">Change Customer</span>
                </button>
              </div>
            )}

            {/* A credit note / invoice pre-filled from this invoice, linked to it
                and (a credit) allocated to it on save. Always shown; disabled
                with the reason where it does not apply. */}
            {['credit', 'debit'].map(kind => {
              const block = adjustActionBlock(kind, invoice, adjustAccess);
              return (
                <div key={`adjust-${kind}`}>
                  <button data-write-action
                    className={`btn-slideout${block ? ' btn-disabled' : ''}`}
                    onClick={() => {
                      // Closed at once (the screen behind is about to change),
                      // then on to Create Invoice with the hand-off.
                      onClose();
                      startAdjustment(navigate, invoice.id, kind);
                    }}
                    disabled={!!block}
                    title={block || ADJUST_ACTIONS[kind].title}
                  >
                    <div className="icon-wrapper">
                      <span className="material-symbols-rounded">{ADJUST_ACTIONS[kind].icon}</span>
                    </div>
                    <span className="btn-label">{ADJUST_ACTIONS[kind].label}</span>
                  </button>
                </div>
              );
            })}

            {/* Offered only where the host screen can navigate — hands the user
                to Search Invoices pre-filtered to this document so they can take
                any action available there. */}
            {openInSearch && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={() => { openInSearch(invoice); handleClose(); }}
                  title="Open this invoice in Search Invoices"
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">manage_search</span>
                  </div>
                  <span className="btn-label">Find in Search Invoices</span>
                </button>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {exporting && invoice?.id && (
        <DocumentOutputModal
          type="invoice"
          ids={[invoice.id]}
          onClose={() => setExporting(false)}
          onDone={(message) => { setExporting(false); if (message) showAlert(message, 'success'); }}
        />
      )}

      {showHistory && invoice && (
        <AuditHistoryModal
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          entityType={invoice.is_credit ? 'credit_note' : 'invoice'}
          entityId={invoice.id}
          entityIdentifier={invoice.invoice_no}
        />
      )}

      {bookingOver && invoice && (
        <BookOverInvoiceFlow
          invoiceId={invoice.id}
          onDone={() => fetchInvoiceDetails()}
          onClose={() => { setBookingOver(false); fetchInvoiceDetails(); }}
        />
      )}

      {changingCustomer && invoice && (
        <ChangeCustomerModal
          invoiceId={invoice.id}
          onClose={() => { setChangingCustomer(false); fetchInvoiceDetails(); }}
          onDone={(result) => {
            setChangingCustomer(false);
            fetchInvoiceDetails();
            showAlert(`${invoice.invoiceNo}: ${describeCustomerChange(result.change)}.`, 'success');
          }}
        />
      )}
    </div>,
    document.body
  );
}

export default InvoiceModal;
